import { useEffect, useMemo, useRef, useState } from "react";

import {
  DEFAULT_MINIMUM_DELAY_MS,
  DEFAULT_SLOW_STATE_DELAY_MS,
} from "js/components/Loading/loadingConfig";

type UsePageLoadingOptions = {
  isLoading: boolean;
  minimumDelayMs?: number;
  slowStateDelayMs?: number;
  onSlowState?: () => void;
  enabled?: boolean;
};

type UsePageLoadingResult = {
  showSkeleton: boolean;
  showSlowState: boolean;
  dismissSlowState: () => void;
};

const usePageLoading = ({
  isLoading,
  minimumDelayMs = DEFAULT_MINIMUM_DELAY_MS,
  slowStateDelayMs = DEFAULT_SLOW_STATE_DELAY_MS,
  onSlowState,
  enabled = true,
}: UsePageLoadingOptions): UsePageLoadingResult => {
  const [showSkeleton, setShowSkeleton] = useState<boolean>(() => enabled && isLoading);
  const [showSlowState, setShowSlowState] = useState<boolean>(false);

  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number | null>(isLoading ? Date.now() : null);

  useEffect(() => {
    if (!enabled) {
      setShowSkeleton(false);
      setShowSlowState(false);
      startTimeRef.current = null;
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      if (slowTimerRef.current) {
        clearTimeout(slowTimerRef.current);
        slowTimerRef.current = null;
      }
      return;
    }

    if (isLoading) {
      startTimeRef.current = Date.now();
      setShowSkeleton(true);
      setShowSlowState(false);

      if (slowStateDelayMs > 0) {
        if (slowTimerRef.current) {
          clearTimeout(slowTimerRef.current);
        }
        slowTimerRef.current = setTimeout(() => {
          setShowSlowState(true);
          if (onSlowState) onSlowState();
        }, slowStateDelayMs);
      }

      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    } else {
      if (slowTimerRef.current) {
        clearTimeout(slowTimerRef.current);
        slowTimerRef.current = null;
      }

      const startedAt = startTimeRef.current ?? Date.now();
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(minimumDelayMs - elapsed, 0);

      hideTimerRef.current = setTimeout(() => {
        setShowSkeleton(false);
        setShowSlowState(false);
        hideTimerRef.current = null;
      }, remaining);
    }

    return () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
      if (slowTimerRef.current) {
        clearTimeout(slowTimerRef.current);
        slowTimerRef.current = null;
      }
    };
  }, [enabled, isLoading, minimumDelayMs, slowStateDelayMs, onSlowState]);

  const dismissSlowState = useMemo(
    () => () => {
      setShowSlowState(false);
      if (slowTimerRef.current) {
        clearTimeout(slowTimerRef.current);
        slowTimerRef.current = null;
      }

      if (!enabled || !isLoading || slowStateDelayMs <= 0) {
        return;
      }

      slowTimerRef.current = setTimeout(() => {
        setShowSlowState(true);
        if (onSlowState) onSlowState();
      }, slowStateDelayMs);
    },
    [enabled, isLoading, slowStateDelayMs, onSlowState],
  );

  return {
    showSkeleton,
    showSlowState: showSkeleton && showSlowState,
    dismissSlowState,
  };
};

export default usePageLoading;
